import * as React from 'react';
import { Control, Controller, ControllerProps, useFormContext } from 'react-hook-form';
import { Box, IconButton, IconButtonProps, InputLabel, Stack, Tooltip } from '@mui/material';
import { styled } from '@mui/system';
import {
    Unstable_NumberInput as BaseNumberInput,
    NumberInputProps,
    numberInputClasses,
} from '@mui/base/Unstable_NumberInput';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import Scale from '@mui/icons-material/Scale';

export interface FormInputProps extends Omit<ControllerProps, 'name' | 'control' | 'render'> {
    name: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    control?: Control<any, any>;
    label?: string;
    numberInputProps?: Partial<NumberInputProps>;
    tooltip?: string;
    units?: string;
}

// NOTE: https://mui.com/material-ui/react-text-field/#type-quot-number-quot
// SOURCE:  https://mui.com/base-ui/react-number-input/#introduction
export default function FormInputNumber({
    name,
    control,
    label,
    numberInputProps,
    tooltip,
    units,
    ...rest
}: FormInputProps) {
    const { trigger } = useFormContext();
    return (
        <Controller
            {...rest}
            name={name}
            control={control}
            render={({ field: { onChange, value, ...restField }, fieldState: { error } }) => (
                <Tooltip open={tooltip ? undefined : false} title={tooltip}>
                    <Stack flexDirection={'row'} justifyContent={'center'} alignItems={'center'}>
                        {/* Box wrapper improves label being hidden when label is long and there is not much space */}
                        <Box>
                            <InputLabel
                                sx={{
                                    fontSize: '.8em',
                                    paddingRight: 1,
                                    textAlign: 'right',
                                    textWrap: 'wrap', // allow label to wrap
                                }}
                            >
                                {label}
                            </InputLabel>
                        </Box>
                        <Box>
                            <NumberInput
                                {...numberInputProps}
                                {...((units?.length && !value) ||
                                (units?.length &&
                                    value &&
                                    ['number', 'string'].includes(typeof value) &&
                                    `${value}`.replace(/[\d.]/g, '').length === 0)
                                    ? {
                                          endAdornment: (
                                              <InputAdornment>
                                                  {units}
                                                  <Scale sx={{ marginLeft: 1 }} />
                                              </InputAdornment>
                                          ),
                                      }
                                    : {})}
                                {...restField}
                                error={!!error}
                                // https://mui.com/base-ui/react-number-input/#introduction
                                onChange={(_e, newValue) => {
                                    onChange(newValue !== null ? Math.max(newValue, 0) : newValue);
                                    // triggering Yup validation on change
                                    trigger?.(name);
                                }}
                                value={typeof value === 'number' ? Math.max(value, 0) : value}
                            />
                        </Box>
                    </Stack>
                </Tooltip>
            )}
        />
    );
}

// SOURCE:  https://mui.com/base-ui/react-number-input/#introduction
const NumberInput = React.forwardRef(function CustomNumberInput(
    props: NumberInputProps,
    ref: React.ForwardedRef<HTMLDivElement>,
) {
    return (
        <BaseNumberInput
            slots={{
                root: InputRoot,
                input: InputElement,
                incrementButton: Button(IncrementButton),
                decrementButton: Button(DecrementButton),
            }}
            {...props}
            ref={ref}
        />
    );
});

// SOURCE:  https://mui.com/base-ui/react-number-input/#introduction
const IncrementButton = React.forwardRef(function CustomIncrementButton(
    props: IconButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
) {
    return (
        <IconButton aria-label="increment" {...props} ref={ref}>
            <ExpandLess />
        </IconButton>
    );
});

// SOURCE:  https://mui.com/base-ui/react-number-input/#introduction
const DecrementButton = React.forwardRef(function CustomDecrementButton(
    props: IconButtonProps,
    ref: React.ForwardedRef<HTMLButtonElement>,
) {
    return (
        <IconButton aria-label="decrement" {...props} ref={ref}>
            <ExpandMore />
        </IconButton>
    );
});

// SOURCE:  https://mui.com/base-ui/react-number-input/#introduction
const InputAdornment = styled('div')(
    ({ theme }) => `
  margin: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  grid-row: 1/3;
  color: ${theme.palette.mode === 'dark' ? grey[500] : grey[700]};
`,
);

const blue = {
    100: '#DAECFF',
    200: '#B6DAFF',
    400: '#3399FF',
    500: '#007FFF',
    600: '#0072E5',
    700: '#0059B2',
    900: '#003A75',
};

const grey = {
    50: '#F3F6F9',
    100: '#E5EAF2',
    200: '#DAE2ED',
    300: '#C7D0DD',
    400: '#B0B8C4',
    500: '#9DA8B7',
    600: '#6B7A90',
    700: '#434D5B',
    800: '#303740',
    900: '#1C2025',
};

const InputRoot = styled('div')(
    ({ theme }) => `
  font-family: 'IBM Plex Sans', sans-serif;
  font-weight: 400;
  border-radius: 8px;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
  box-shadow: 0px 2px 4px ${
      theme.palette.mode === 'dark' ? 'rgba(0,0,0, 0.5)' : 'rgba(0,0,0, 0.05)'
  };
  display: grid;
  grid-template-columns: auto 1fr auto 25px;
  grid-template-rows: 1fr 1fr;
  overflow: hidden;
  padding: 4px;
  margin: 2px 0px;

  &.${numberInputClasses.focused} {
    border-color: ${blue[400]};
    box-shadow: 0 0 0 3px ${theme.palette.mode === 'dark' ? blue[700] : blue[200]};
  }

  &:hover {
    border-color: ${blue[400]};
  }

  // firefox
  &:focus-visible {
    outline: 0;
  }
`,
);

const InputElement = styled('input')(
    ({ theme }) => `
  font-size: 0.875rem;
  font-family: inherit;
  font-weight: 400;
  line-height: 1.5;
  grid-row: 1/3;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  background: inherit;
  border: none;
  border-radius: inherit;
  padding: 8px 8px;
  outline: 0;
`,
);

// const Button = styled('button')(
const Button = (C: React.JSXElementConstructor<IconButtonProps>) =>
    styled(C)(
        ({ theme }) => `
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  align-items: center;
  appearance: none;
  padding: 0;
  width: 19px;
  height: 20px;
  font-family: system-ui, sans-serif;
  font-size: 0.875rem;
  line-height: 1;
  box-sizing: border-box;
  background: ${theme.palette.mode === 'dark' ? grey[900] : '#fff'};
  border: 0;
  color: ${theme.palette.mode === 'dark' ? grey[300] : grey[900]};
  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  &:hover {
    background: ${theme.palette.mode === 'dark' ? grey[800] : grey[50]};
    border-color: ${theme.palette.mode === 'dark' ? grey[600] : grey[300]};
    cursor: pointer;
  }

  &.${numberInputClasses.incrementButton} {
    grid-column: 4/5;
    grid-row: 1/2;
    border-bottom-left-radius: 1px;
    border-bottom-right-radius: 1px;
    border: 1px solid;
    border-bottom: 0;
    border-color: ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
    background: ${theme.palette.mode === 'dark' ? grey[900] : grey[50]};
    color: ${theme.palette.mode === 'dark' ? grey[200] : grey[900]};

    &:hover {
      cursor: pointer;
      color: #FFF;
      background: ${theme.palette.mode === 'dark' ? blue[600] : blue[500]};
      border-color: ${theme.palette.mode === 'dark' ? blue[400] : blue[600]};
    }
  }

  &.${numberInputClasses.decrementButton} {
    grid-column: 4/5;
    grid-row: 2/3;
    border-top-left-radius: 1px;
    border-top-right-radius: 1px;
    border: 1px solid;
    border-color: ${theme.palette.mode === 'dark' ? grey[700] : grey[200]};
    background: ${theme.palette.mode === 'dark' ? grey[900] : grey[50]};
    color: ${theme.palette.mode === 'dark' ? grey[200] : grey[900]};

    &:hover {
      cursor: pointer;
      color: #FFF;
      background: ${theme.palette.mode === 'dark' ? blue[600] : blue[500]};
      border-color: ${theme.palette.mode === 'dark' ? blue[400] : blue[600]};
    }
  }

  & .arrow {
    transform: translateY(-1px);
  }

  & .arrow {
    transform: translateY(-1px);
  }
`,
    );
